import React from "react";
import { hydrateRoot, createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// Prerendered routes: hydrate (keep static HTML visible for instant FCP/SEO).
// Non-matching routes: clear and render fresh.
// Hydration mismatches from Zustand persist / browser-only state are silenced
// by suppressHydrationWarning on the root wrapper in AppShell.
if (typeof document !== "undefined") {
  const root = document.getElementById("root")!;
  const marker = root.querySelector("[data-prerender-path]");
  // Normalize trailing slash so /product/foo/ matches prerendered /product/foo
  const prerenderPath = marker?.getAttribute("data-prerender-path") ?? "";
  const currentPath = window.location.pathname;
  const stripSlash = (p: string) => p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
  const hasMatchingPrerender =
    marker && stripSlash(prerenderPath) === stripSlash(currentPath);

  if (hasMatchingPrerender && root.children.length > 0) {
    hydrateRoot(root, <App />, { onRecoverableError: () => {} });
  } else {
    root.innerHTML = "";
    createRoot(root).render(<App />);
  }
}

const STATIC_ROUTES = [
  "/o/hydration-guide",
  "/o/serum",
  "/pages/contact-us",
  "/pages/privacy-policy",
  "/pages/terms-of-service",
  "/pages/shipping",
  "/pages/returns",
  "/pages/data-sharing-opt-out",
  "/pages/track-your-order",
  "/blog",
];

const SHOPIFY_STORE_DOMAIN = "oaria-2.myshopify.com";
const SHOPIFY_API_VERSION = "2025-07";
const SHOPIFY_STOREFRONT_TOKEN = "a2e9ef8c1048978b2903844376c06384";

async function shopifyQuery(query: string, variables: Record<string, unknown>) {
  const res = await fetch(
    `https://${SHOPIFY_STORE_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    }
  );
  return res.json();
}

async function fetchDynamicRoutes(): Promise<string[]> {
  const routes: string[] = [];

  try {
    // Fetch product handles (paginated)
    let after: string | null = null;
    let hasNext = true;
    while (hasNext) {
      const data = await shopifyQuery(
        `query ($first: Int!, $after: String) {
          products(first: $first, after: $after) {
            edges { node { handle } }
            pageInfo { hasNextPage endCursor }
          }
        }`,
        { first: 250, after }
      );
      const edges = data?.data?.products?.edges || [];
      for (const { node } of edges) {
        routes.push(`/product/${node.handle}`);
      }
      hasNext = data?.data?.products?.pageInfo?.hasNextPage ?? false;
      after = data?.data?.products?.pageInfo?.endCursor ?? null;
    }

    // Fetch blog articles
    const blogData = await shopifyQuery(
      `query ($first: Int!) {
        blogs(first: $first) {
          edges {
            node {
              handle
              articles(first: 250, sortKey: PUBLISHED_AT, reverse: true) {
                edges { node { handle } }
              }
            }
          }
        }
      }`,
      { first: 10 }
    );
    const blogs = blogData?.data?.blogs?.edges || [];
    for (const { node: blog } of blogs) {
      for (const { node: article } of blog.articles?.edges || []) {
        routes.push(`/blog/${blog.handle}/${article.handle}`);
      }
    }
  } catch (e) {
    console.warn("[prerender] Failed to fetch dynamic routes:", (e as Error).message);
  }

  return routes;
}

// Prerender function called by vite-prerender-plugin at build time
export async function prerender(data: { url: string }) {
  const { renderToString } = await import("react-dom/server");
  const { StaticRouter } = await import("react-router-dom/server");
  const { getPageLoader, PrerenderWrapper } = await import("./PrerenderShell");

  // On first call (homepage), fetch dynamic routes and return all links
  let allRoutes = [...STATIC_ROUTES];
  if (data.url === "/") {
    const dynamicRoutes = await fetchDynamicRoutes();
    allRoutes = [...STATIC_ROUTES, ...dynamicRoutes];
    console.log(`[prerender] Discovered ${dynamicRoutes.length} dynamic routes`);
  }

  let prerenderArticleState: unknown = null;
  let prerenderArticleListState: unknown[] | null = null;

  // Fetch single blog article content for blog post prerendering
  const blogMatch = data.url.match(/^\/blog\/([^/]+)\/([^/]+)\/?$/);
  if (blogMatch) {
    const [, blogHandle, articleHandle] = blogMatch;
    try {
      const query = [
        "query ($blogHandle: String!) {",
        "  blog(handle: $blogHandle) {",
        "    articles(first: 50) {",
        "      edges {",
        "        node {",
        "          title handle contentHtml publishedAt excerpt tags",
        "          image { url altText }",
        "          seo { title description }",
        "        }",
        "      }",
        "    }",
        "  }",
        "}",
      ].join("\n");
      const articleData = await shopifyQuery(query, { blogHandle });
      const edges = articleData?.data?.blog?.articles?.edges || [];
      const articleNode = edges.find((e: any) => e.node.handle === articleHandle)?.node;
      if (articleNode) {
        const withBlog = { ...articleNode, blog: { handle: blogHandle } };
        (globalThis as any).__PRERENDER_ARTICLE__ = withBlog;
        prerenderArticleState = withBlog;
      } else {
        console.warn(`[prerender] article ${articleHandle} not found (edges=${edges.length})`);
      }
    } catch (e) {
      console.warn("[prerender] Failed to fetch blog article:", (e as Error).message);
    }
  }
  // Fetch blog article list for /blog index prerendering
  if (data.url === "/blog" || data.url === "/blog/") {
    try {
      const listData = await shopifyQuery(`
        query ($first: Int!) {
          blogs(first: $first) {
            edges {
              node {
                handle
                articles(first: 50, sortKey: PUBLISHED_AT, reverse: true) {
                  edges {
                    node {
                      id title handle excerpt publishedAt
                      image { url altText }
                    }
                  }
                }
              }
            }
          }
        }
      `, { first: 10 });
      const blogs = listData?.data?.blogs?.edges || [];
      const allArticles: unknown[] = [];
      for (const { node: blog } of blogs) {
        for (const { node: article } of blog.articles?.edges || []) {
          allArticles.push({ ...article, blog: { handle: blog.handle } });
        }
      }
      allArticles.sort((a: any, b: any) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
      (globalThis as any).__PRERENDER_ARTICLE_LIST__ = allArticles;
      prerenderArticleListState = allArticles;
    } catch (e) {
      console.warn("[prerender] Failed to fetch blog article list:", (e as Error).message);
    }
  }

  const loader = getPageLoader(data.url);
  let html = "";
  let helmetHtml = "";
  let helmetTitle = "";
  if (loader) {
    try {
      const { default: Page } = await loader();
      const helmetCtx: any = {};
      const inner = renderToString(
        <StaticRouter location={data.url}>
        <PrerenderWrapper url={data.url} helmetContext={helmetCtx}>
          <Page />
        </PrerenderWrapper>
        </StaticRouter>
      );
      // Wrap with route marker so the client can detect matching prerender
      html = `<div data-prerender-path="${data.url}">${inner}</div>`;
      // Serialize Helmet head tags (title, meta, canonical, JSON-LD) for static injection
      const helmet = helmetCtx.helmet;
      if (helmet) {
        const decode = (s: string) => s.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#x27;|&#39;/g, "'");
        const titleMatch = helmet.title.toString().match(/<title[^>]*>([^<]*)<\/title>/);
        if (titleMatch) helmetTitle = decode(titleMatch[1]);
        helmetHtml = [
          helmet.meta.toString(),
          helmet.link.toString(),
          helmet.script.toString(),
        ].join("\n");
      }
    } catch (e) {
      console.warn(`[prerender] Skipping ${data.url}:`, (e as Error).message);
    }
  }

  if (prerenderArticleState) {
    const serialized = JSON.stringify(prerenderArticleState).replace(/</g, "\\u003c");
    html += `<script>window.__PRERENDER_ARTICLE__=${serialized};</script>`;
  }
  if (prerenderArticleListState) {
    const serialized = JSON.stringify(prerenderArticleListState).replace(/</g, "\\u003c");
    html += `<script>window.__PRERENDER_ARTICLE_LIST__=${serialized};</script>`;
  }

  const head = helmetTitle ? { title: helmetTitle, elements: helmetHtml ? helmetHtml.split("\n").filter(Boolean) : [] } : {};
  return { html, head, links: allRoutes };
}
